import {Amenity} from './amenity';
import {Building} from './building';
import {GeoLocation} from './geoLocation';

/**
 * Property type enum
 * @export
 * @enum {string}
 */
export enum PropertyType {
  MULTI_FAMILY = 'multi-family',
  COMMERCIAL = 'commercial',
}

/**
 *
 * @export
 * @interface Apartment
 */
export interface Apartment {
  /**
   *
   * @type {Array<Amenity>}
   * @memberof Apartment
   */
  amenities?: Array<Amenity>;
  /**
   *
   * @type {Array<Building>}
   * @memberof Apartment
   */
  buildings?: Array<Building>;
  /**
   *
   * @type {string}
   * @memberof Apartment
   */
  city?: string;
  /**
   * 创建日期
   * @type {string}
   * @memberof Apartment
   */
  createdAt?: string;
  /**
   * 公寓描述信息
   * @type {string}
   * @memberof Apartment
   */
  description?: string;
  /**
   *
   * @type {string}
   * @memberof Apartment
   */
  id?: string;
  /**
   * 公寓位置
   * @type {GeoLocation}
   * @memberof Apartment
   */
  location?: GeoLocation;
  /**
   * 公寓名字
   * @type {string}
   * @memberof Apartment
   */
  name?: string;
  /**
   * 所属组织
   * @type {string}
   * @memberof Apartment
   */
  orgId?: string;
  /**
   * 站点
   * @type {string}
   * @memberof Apartment
   */
  siteId?: string;
  /**
   *
   * @type {string}
   * @memberof Apartment
   */
  state?: string;
  /**
   *
   * @type {string}
   * @memberof Apartment
   */
  street?: string;
  /**
   * 修改日期
   * @type {string}
   * @memberof Apartment
   */
  updatedAt?: string;
  /**
   *
   * @type {string}
   * @memberof Apartment
   */
  zipCode?: string;
  /**
   * 公寓联系电话
   * @type {string}
   * @memberof Apartment
   */
  mobile?: string;
  /**
   * Property type
   * @type {PropertyType}
   * @memberof Apartment
   */
  propertyType?: PropertyType;
  /**
   * IANA timezone string (e.g. "America/New_York")
   * @type {string}
   * @memberof Apartment
   */
  timeZone?: string;
}
/**
 *
 * @export
 * @interface ApartmentCreateReq
 */
export interface ApartmentCreateReq {
  /**
   *
   * @type {Apartment}
   * @memberof ApartmentCreateReq
   */
  apartment?: Apartment;
}
/**
 *
 * @export
 * @interface ApartmentUpdateReq
 */
export interface ApartmentUpdateReq {
  /**
   *
   * @type {Apartment}
   * @memberof ApartmentUpdateReq
   */
  apartment?: Apartment;
  /**
   *
   * @type {string}
   * @memberof ApartmentUpdateReq
   */
  apartmentId?: string;
}
/**
 *
 * @export
 * @interface Apartments
 */
export interface Apartments {
  /**
   *
   * @type {Array<Apartment>}
   * @memberof Apartments
   */
  apartments?: Array<Apartment>;
  /**
   *
   * @type {number}
   * @memberof Apartments
   */
  total?: number;
}

/**
 * Bulk grant apartment permissions request
 * @export
 * @interface ApartmentPermissionBulkGrantReq
 */
export interface ApartmentPermissionBulkGrantReq {
  /**
   * Array of apartment IDs to grant permissions to
   * @type {Array<string>}
   * @memberof ApartmentPermissionBulkGrantReq
   */
  apartmentIds: Array<string>;
}

/**
 * Bulk revoke apartment permissions request
 * @export
 * @interface ApartmentPermissionBulkRevokeReq
 */
export interface ApartmentPermissionBulkRevokeReq {
  /**
   * Array of apartment IDs to revoke permissions from
   * @type {Array<string>}
   * @memberof ApartmentPermissionBulkRevokeReq
   */
  apartmentIds: Array<string>;
}

/**
 * Apartment permission bulk grant response
 * @export
 * @interface ApartmentPermissionBulkGrantRsp
 */
export interface ApartmentPermissionBulkGrantRsp {
  /**
   * Success message
   * @type {string}
   * @memberof ApartmentPermissionBulkGrantRsp
   */
  message: string;
  /**
   * User ID
   * @type {string}
   * @memberof ApartmentPermissionBulkGrantRsp
   */
  userId: string;
  /**
   * Total number of apartments requested
   * @type {number}
   * @memberof ApartmentPermissionBulkGrantRsp
   */
  totalRequested: number;
  /**
   * Number of new permissions granted
   * @type {number}
   * @memberof ApartmentPermissionBulkGrantRsp
   */
  newPermissions: number;
  /**
   * Number of permissions that already existed
   * @type {number}
   * @memberof ApartmentPermissionBulkGrantRsp
   */
  alreadyExisting: number;
  /**
   * Array of apartment IDs that were granted permissions
   * @type {Array<string>}
   * @memberof ApartmentPermissionBulkGrantRsp
   */
  grantedApartments: Array<string>;
  /**
   * Array of apartment IDs that were skipped (already had permissions)
   * @type {Array<string>}
   * @memberof ApartmentPermissionBulkGrantRsp
   */
  skippedApartments: Array<string>;
}

/**
 * Apartment permission bulk update response
 * @export
 * @interface ApartmentPermissionBulkUpdateRsp
 */
export interface ApartmentPermissionBulkUpdateRsp {
  /**
   * Success message
   * @type {string}
   * @memberof ApartmentPermissionBulkUpdateRsp
   */
  message: string;
  /**
   * User ID
   * @type {string}
   * @memberof ApartmentPermissionBulkUpdateRsp
   */
  userId: string;
  /**
   * Number of apartment permissions
   * @type {number}
   * @memberof ApartmentPermissionBulkUpdateRsp
   */
  apartmentCount: number;
}

/**
 * Apartment permission bulk revoke response
 * @export
 * @interface ApartmentPermissionBulkRevokeRsp
 */
export interface ApartmentPermissionBulkRevokeRsp {
  /**
   * Success message
   * @type {string}
   * @memberof ApartmentPermissionBulkRevokeRsp
   */
  message: string;
  /**
   * User ID
   * @type {string}
   * @memberof ApartmentPermissionBulkRevokeRsp
   */
  userId: string;
  /**
   * Number of permissions revoked
   * @type {number}
   * @memberof ApartmentPermissionBulkRevokeRsp
   */
  revokedCount: number;
  /**
   * Array of apartment IDs that were requested for revocation
   * @type {Array<string>}
   * @memberof ApartmentPermissionBulkRevokeRsp
   */
  requestedApartments: Array<string>;
}

/**
 * User with apartment permission
 * @export
 * @interface ApartmentPermissionUser
 */
export interface ApartmentPermissionUser {
  /**
   * User ID
   * @type {string}
   * @memberof ApartmentPermissionUser
   */
  id?: string;
  /**
   * User email
   * @type {string}
   * @memberof ApartmentPermissionUser
   */
  email?: string;
  /**
   * User first name
   * @type {string}
   * @memberof ApartmentPermissionUser
   */
  firstName?: string;
  /**
   * User last name
   * @type {string}
   * @memberof ApartmentPermissionUser
   */
  lastName?: string;
  /**
   * User status
   * @type {string}
   * @memberof ApartmentPermissionUser
   */
  status?: string;
}

/**
 * Apartment permission entry
 * @export
 * @interface ApartmentPermission
 */
export interface ApartmentPermission {
  /**
   * Permission ID
   * @type {string}
   * @memberof ApartmentPermission
   */
  permissionId?: string;
  /**
   * User information
   * @type {ApartmentPermissionUser}
   * @memberof ApartmentPermission
   */
  user?: ApartmentPermissionUser;
}

/**
 * Get apartment permissions response
 * @export
 * @interface ApartmentPermissionsRsp
 */
export interface ApartmentPermissionsRsp {
  /**
   * Array of apartment permissions
   * @type {Array<ApartmentPermission>}
   * @memberof ApartmentPermissionsRsp
   */
  data?: Array<ApartmentPermission>;
}
